import { GridSortItem } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useState } from "react";
import CustomDataGridV2 from "@/components/CustomDataGridV2";
import { GlobalTableHeightWrapper } from "@/components/GlobalTableHeightWrapper";
import ActionMenu from "@/components/app/ActionMenu";
import DeleteModal from "@/components/app/DeleteModal";
import { ViewRecord } from "@/dummy-data/data-import";
import { useArchiveState } from "@/hooks/useArchiveState";
import { useVisitNoteTemplateGetAll } from "@/queries/practice-setting/template/use-visit-note-template-get-all";
import { useArchiveVisitNoteTemplate } from "@/queries/practice-setting/template/use-visit-note-template-archive";
import { NonNullableDate } from "@/utils/date-time-utility";
import {
  DEF_PAGINATION,
  DGT_Pagination,
  applyDefaultColumnSettings,
  getXtenantId,
  replaceUnderScoreAddSpace,
} from "@/utils/utils";
import { VisitNoteFormValues } from "./VisitNoteTemplateForm";
import {
  VISIT_NOTE_TEMPLATE_ACTION,
  VISIT_NOTE_TEMPLATE_ACTION_LABEL,
  VISIT_NOTE_TEMPLATE_ARCHIVE_CONFIRMATION,
  VISIT_NOTE_TEMPLATE_UNARCHIVE_CONFIRMATION,
} from "./visit-note-template-constant";

interface VisitNoteTableTemplateProps {
  setSelectedVisitNote?: Dispatch<
    SetStateAction<VisitNoteFormValues | undefined>
  >;
  setCurView: (v: "edit" | "view") => void;
  showArchived: boolean;
}

const VisitNoteTableTemplate = ({
  setSelectedVisitNote,
  setCurView,
  showArchived,
}: VisitNoteTableTemplateProps) => {
  const [pagination, setPagination] = useState<DGT_Pagination>(DEF_PAGINATION);
  const [gridSorting, setGridSorting] = useState<GridSortItem[]>([]);
  const { data: templateData, refetch } = useVisitNoteTemplateGetAll({
    pagination,
    gridSorting: gridSorting,
    archive: showArchived,
  });
  const { mutateAsync: archiveProvider } = useArchiveVisitNoteTemplate();
  const { deleteDisc, archiveHandler } = useArchiveState(archiveProvider);

  const COLUMNS = applyDefaultColumnSettings<ViewRecord>([
    {
      field: "templateName",
      headerName: "Template Name",
      flex: 0.5,
      width: 150,
      sortable: false,
      renderCell: ({ value }) => value || "-",
    },
    {
      field: "templateType",
      headerName: "Visit Note Type",
      flex: 0.5,
      width: 240,
      sortable: false,
      renderCell: ({ value }) =>
        value ? replaceUnderScoreAddSpace(value?.toLowerCase()) : "-",
    },
    {
      field: "createdAt",
      headerName: "Created Date",
      flex: 0.4,
      width: 150,
      sortable: false,
      renderCell: ({ value }) => NonNullableDate(value),
    },
    {
      field: "Action",
      flex: 0.1,
      width: 60,
      sortable: false,
      renderCell: ({ row }) => {
        return (
          <ActionMenu
            options={[
              {
                value: VISIT_NOTE_TEMPLATE_ACTION.VIEW,
                label: VISIT_NOTE_TEMPLATE_ACTION_LABEL.VIEW,
              },
              {
                value: VISIT_NOTE_TEMPLATE_ACTION.EDIT,
                label: VISIT_NOTE_TEMPLATE_ACTION_LABEL.EDIT,
              },
              {
                value: VISIT_NOTE_TEMPLATE_ACTION.ARCHIVE,
                label: showArchived
                  ? VISIT_NOTE_TEMPLATE_ACTION_LABEL.UNARCHIVE
                  : VISIT_NOTE_TEMPLATE_ACTION_LABEL.ARCHIVE,
              },
            ]}
            onActionClick={(v) => {
              setSelectedVisitNote?.(row as any);
              if (v === VISIT_NOTE_TEMPLATE_ACTION.VIEW) {
                setCurView("view");
              } else if (v === VISIT_NOTE_TEMPLATE_ACTION.EDIT) {
                setCurView("edit");
              } else {
                deleteDisc.open(row?.uuid);
              }
            }}
          />
        );
      },
    },
  ]);

  return (
    <>
      <DeleteModal
        w="380px"
        open={deleteDisc.opened}
        onClose={deleteDisc.close}
        id={deleteDisc.deleteId}
        onConfirmation={(id) =>
          archiveHandler(!showArchived, id, getXtenantId()).then((res) => {
            refetch();
            return res;
          })
        }
        content={
          showArchived
            ? VISIT_NOTE_TEMPLATE_UNARCHIVE_CONFIRMATION
            : VISIT_NOTE_TEMPLATE_ARCHIVE_CONFIRMATION
        }
      />

      <GlobalTableHeightWrapper height={"calc(100vh - 180px)"}>
        <CustomDataGridV2
          getRowId={(row) => row?.uuid}
          rows={templateData?.data?.content ?? []}
          rowCount={templateData?.data?.page?.totalElements ?? 10}
          columns={COLUMNS}
          disableRowSelectionOnClick
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          sortModel={gridSorting}
          onSortModelChange={setGridSorting}
        />
      </GlobalTableHeightWrapper>
    </>
  );
};

export default VisitNoteTableTemplate;
